import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import InputAdornment from "@material-ui/core/InputAdornment";
import PhoneIcon from "@material-ui/icons/Phone";
import EmailIcon from "@material-ui/icons/Email";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import CustomInput from "components/CustomInput/CustomInput.js";

import ProfilesApi from "../../../api_calls/ApiServices";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";

const MAX_PHONE_LENGTH = 16;

// Makes the MUI v6 Autocomplete fields (Country, Code) look like the
// template's MUI v4 selects next to them (Gender, Region) — see
// customSelectStyle.js: selectLabel, select, selectFormControl.
const selectLikeSx = {
  m: "7px 0 17px 0",
  "& .MuiInputLabel-root": {
    fontSize: "12px",
    textTransform: "uppercase",
    color: "#3C4858",
    top: "8px",
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#3C4858" },
  "& .MuiInputLabel-root.Mui-error": { color: "#f44336" },
  "& .MuiInput-input": {
    fontSize: ".75rem",
    textTransform: "uppercase",
    color: "#3C4858",
    padding: "12px 0 7px !important",
  },
  "& .MuiInput-underline:before, & .MuiInput-underline:hover:not(.Mui-disabled):before": {
    borderBottom: "1px solid #D2D2D2",
  },
  "& .MuiInput-underline:after": { borderBottomColor: "#9c27b0" },
};

const styles = {
  infoText: {
    fontWeight: "300",
    margin: "10px 0 30px",
    textAlign: "center",
  },
  inputAdornmentIcon: {
    color: "#555",
  },
  errorText: {
    color: "red",
  },
  ...customSelectStyle,
};

const useStyles = makeStyles(styles);

const Step2 = forwardRef((props, ref) => {
  const classes = useStyles();
  const [gender, setGender] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  // isoCode of the country whose dialling code prefixes the phone number.
  const [dialCountry, setDialCountry] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countryData = await ProfilesApi.fetchCountries();
        setCountries(
          countryData.map(({ name, isoCode, callingCode }) => ({
            isoCode,
            name,
            value: callingCode,
          }))
        );
      } catch (error) {
        console.error("Failed to fetch countries:", error);
      }
    };
    loadCountries();
  }, []);

  useEffect(() => {
    if (!country) {
      setRegions([]);
      setRegion("");
      return;
    }

    const loadRegions = async () => {
      try {
        setRegions([]);
        setRegion("");

        const selectedCountry = countries.find((c) => c.isoCode === country);
        if (!selectedCountry) return;

        const areaData = await ProfilesApi.fetchCountryAreas(
          selectedCountry.isoCode
        );
        setRegions(areaData);
      } catch (error) {
        console.error("Failed to fetch regions:", error);
        setRegions([]);
      }
    };

    loadRegions();
  }, [country, countries]);

  useEffect(() => {
    if (!regions.some((r) => r.id === region)) {
      setRegion("");
    }
  }, [regions, region]);

  // Default the dialling code to the chosen country until the user picks
  // one explicitly.
  useEffect(() => {
    if (country && !dialCountry) setDialCountry(country);
  }, [country, dialCountry]);

  const findCountry = (isoCode) =>
    countries.find((c) => c.isoCode === isoCode) || null;

  // Case-insensitive "contains" match on name or dialling code, so typing
  // "ken", "254" or "+254" all find Kenya.
  const filterCountries = (options, { inputValue }) => {
    const query = inputValue.trim().toLowerCase().replace(/^\+/, "");
    if (!query) return options;
    return options.filter(
      ({ name, value }) =>
        name.toLowerCase().includes(query) || String(value).startsWith(query)
    );
  };

  // +<calling code><local number>, dropping a trunk "0" (e.g. 0712… -> +254712…).
  const fullPhoneNumber = () => {
    const code = findCountry(dialCountry)?.value;
    if (!code || phoneNumber.startsWith("+")) return phoneNumber;
    return `+${code}${phoneNumber.replace(/^0/, "")}`;
  };

  // ----------------------------
  // PHONE INPUT ENFORCEMENT
  // ----------------------------

  const handlePhoneChange = (e) => {
    let value = e.target.value;

    // allow only digits and one leading +
    value = value.replace(/(?!^\+)[^\d]/g, "");

    // enforce max length
    if (value.length > MAX_PHONE_LENGTH) {
      value = value.slice(0, MAX_PHONE_LENGTH);
    }

    setPhoneNumber(value);
  };

  const handlePhoneKeyDown = (e) => {
    if (
      !/[0-9]/.test(e.key) &&
      e.key !== "+" &&
      e.key !== "Backspace" &&
      e.key !== "Delete" &&
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight" &&
      e.key !== "Tab"
    ) {
      e.preventDefault();
    }

    // prevent + if it's not first char
    if (e.key === "+" && phoneNumber.length > 0) {
      e.preventDefault();
    }

    // prevent typing beyond max length
    if (
      phoneNumber.length >= MAX_PHONE_LENGTH &&
      e.key !== "Backspace" &&
      e.key !== "Delete" &&
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight" &&
      e.key !== "Tab"
    ) {
      e.preventDefault();
    }
  };

  // ----------------------------
  // VALIDATION
  // ----------------------------

  const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

  const validateFields = () => {
    const newErrors = {};

    if (!gender) newErrors.gender = "Gender is required";
    if (!phoneNumber) newErrors.phoneNumber = "Phone number is required";
    else if (!dialCountry && !phoneNumber.startsWith("+"))
      newErrors.phoneNumber = "Select your country code";

    if (!email) newErrors.email = "Email is required";
    else if (!isValidEmail(email))
      newErrors.email = "Enter a valid email address";

    if (!country) newErrors.country = "Country is required";
    if (!region) newErrors.region = "Region is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    sendState: () => {
      if (validateFields()) {
        return {
          gender,
          phoneNumber: fullPhoneNumber(),
          email,
          country,
          region,
        };
      }
      return null;
    },
    validateBeforeNext: validateFields,
  }));

  return (
    <div>
      <h4 className={classes.infoText}>Tell us more about yourself</h4>
      <GridContainer>
        {/* Gender */}
        <GridItem xs={12} sm={6}>
          <FormControl
            fullWidth
            className={classes.selectFormControl}
            error={!!errors.gender}
          >
            <InputLabel className={classes.selectLabel}>Gender</InputLabel>
            <Select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              MenuProps={{ className: classes.selectMenu }}
              classes={{ select: classes.select }}
            >
              <MenuItem disabled value="">
                Select Gender
              </MenuItem>
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
            </Select>
            {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
          </FormControl>
        </GridItem>

        {/* Email */}
        <GridItem xs={12} sm={6}>
          <CustomInput
            labelText="Email Address"
            id="email"
            formControlProps={{ fullWidth: true, error: !!errors.email }}
            inputProps={{
              value: email,
              onChange: (e) => setEmail(e.target.value),
              endAdornment: (
                <InputAdornment position="end">
                  <EmailIcon className={classes.inputAdornmentIcon} />
                </InputAdornment>
              ),
            }}
          />
          {errors.email && <p className={classes.errorText}>{errors.email}</p>}
        </GridItem>

        {/* Country (searchable) */}
        <GridItem xs={12} sm={6}>
          <Autocomplete
            options={countries}
            value={findCountry(country)}
            onChange={(e, option) => setCountry(option?.isoCode || "")}
            filterOptions={filterCountries}
            getOptionLabel={(option) => option.name}
            renderOption={(optionProps, option) => (
              <li {...optionProps} key={option.isoCode}>
                {option.name} (+{option.value})
              </li>
            )}
            isOptionEqualToValue={(option, value) =>
              option.isoCode === value.isoCode
            }
            noOptionsText="No matching country"
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label="Country"
                placeholder="Type to search"
                error={!!errors.country}
                helperText={errors.country}
                sx={selectLikeSx}
              />
            )}
          />
        </GridItem>

        {/* Region */}
        <GridItem xs={12} sm={6}>
          <FormControl
            fullWidth
            className={classes.selectFormControl}
            error={!!errors.region}
          >
            <InputLabel className={classes.selectLabel}>Region</InputLabel>
            <Select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              MenuProps={{ className: classes.selectMenu }}
              classes={{ select: classes.select }}
              disabled={!regions.length}
            >
              <MenuItem disabled value="">
                Select Region
              </MenuItem>
              {regions.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </Select>
            {errors.region && <FormHelperText>{errors.region}</FormHelperText>}
          </FormControl>
        </GridItem>

        {/* Phone: searchable dialling code (defaults to the country) + number */}
        <GridItem xs={12} sm={6}>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
            <Autocomplete
              sx={{ width: 120, flexShrink: 0 }}
              options={countries}
              value={findCountry(dialCountry)}
              onChange={(e, option) => setDialCountry(option?.isoCode || "")}
              filterOptions={filterCountries}
              getOptionLabel={(option) => `+${option.value}`}
              renderOption={(optionProps, option) => (
                <li {...optionProps} key={option.isoCode}>
                  {option.name} (+{option.value})
                </li>
              )}
              isOptionEqualToValue={(option, value) =>
                option.isoCode === value.isoCode
              }
              noOptionsText="No matching country"
              disableClearable={!!dialCountry}
              slotProps={{ popper: { style: { minWidth: 280 } } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label="Code"
                  placeholder="Search"
                  error={!!errors.phoneNumber && !dialCountry}
                  sx={selectLikeSx}
                />
              )}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CustomInput
                labelText="Phone Number"
                id="phone-number"
                formControlProps={{
                  fullWidth: true,
                  error: !!errors.phoneNumber,
                }}
                inputProps={{
                  value: phoneNumber,
                  onChange: handlePhoneChange,
                  onKeyDown: handlePhoneKeyDown,
                  inputMode: "tel",
                  maxLength: MAX_PHONE_LENGTH,
                  endAdornment: (
                    <InputAdornment position="end">
                      <PhoneIcon className={classes.inputAdornmentIcon} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
          {errors.phoneNumber && (
            <p className={classes.errorText}>{errors.phoneNumber}</p>
          )}
        </GridItem>
      </GridContainer>
    </div>
  );
});

Step2.displayName = "Step2";
export default Step2;
